import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Box, Tab, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { WaveBG } from "../../../../../../AI/TitleAnimation/WaveBG";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Interview } from "./Interview";
import { Analysis } from "./Analysis";
import { UnsavedChanges } from "../../../../../../Modal/Confirmation/UnsavedChanges/UnsavedChanges";
import { Evidence } from "./Evidence";

export const Evaluation = forwardRef(
  (
    {
      interview,
      alias,
      isLoading,
      setIsLoading,
      setInterviews,
      goHome,
      isMobil,
      tabValue,
      setTabValue,
      readOnly = false,
    },
    ref
  ) => {
    const theme = useTheme();
    const [t] = useTranslation("investigation");

    const evidenceRef = useRef(null);
    const analysisRef = useRef(null);
    const pendingActionRef = useRef(null);

    const [openCancelDialog, setOpenCancelDialog] = useState(false);

    const hdlChangeTab = (e, newValue) => {
      setTabValue(newValue);
    };

    // Expose handleGoHome to the parent; delegate validation/status to the child (prepareForNavigation)
    useImperativeHandle(
      ref,
      () => ({
        handleGoHome: () => {
          // Fallback: ask if there are unsaved changes
          const childHasChanges = analysisRef.current?.hasUnsavedChanges?.();
          if (childHasChanges) {
            pendingActionRef.current = () => goHome();
            setOpenCancelDialog(true);
          } else {
            goHome();
          }
        },
        handleAddNewFile: () => {
          evidenceRef.current?.addNewFile?.();
        },
        handleAddConclusion: () => {
          analysisRef.current?.hdlOpenConclusion();
        },
      }),
      [goHome]
    );

    const confirmDiscard = () => {
      //  child restores its snapshot / resets values
      try {
        analysisRef.current?.restoreSnapshot?.();
      } catch (e) {
        // noop
      }
      setOpenCancelDialog(false);
      // Perform the pending action (e.g., navigate)
      pendingActionRef.current?.();
      pendingActionRef.current = null;
    };

    const cancelDiscard = () => {
      pendingActionRef.current = null;
      setOpenCancelDialog(false);
    };

    const hasQnA = (interview?.interview?.length ?? 0) > 0;

    // En readOnly ocultamos la tab "analysis" si no hay Q&A; sin Q&A tampoco
    // puede haber evaluación.
    const showAnalysisTab = !readOnly || hasQnA;

    const tabs = [
      showAnalysisTab && {
        label: t("Interview.evaluation"),
        value: "analysis",
        component: (
          <Analysis
            ref={analysisRef}
            evaluation={interview.evaluation}
            conclusion={interview.conclusion}
            id={interview.id}
            interviewed={interview.interviewed}
            setInterviews={setInterviews}
            goHome={goHome}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            alias={alias}
            readOnly={readOnly}
          />
        ),
      },
      {
        label: t("Interview.interview"),
        value: "interview",
        component: (
          <Interview
            objective={interview?.details || ""}
            interview={interview.interview}
            interviewed={interview.interviewed}
            interviewer={interview.interviewer}
            witness={interview.witness || []}
          />
        ),
      },
      {
        label: t("Interview.evidence_files"),
        value: "attachments",
        component: (
          <Evidence
            ref={evidenceRef}
            evidence={interview.attachments}
            id={interview.id}
            setInterviews={setInterviews}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            alias={alias}
            readOnly={readOnly}
          />
        ),
      },
    ].filter(Boolean);

    return (
      <Box
        height={"100%"}
        overflow={"auto"}
        width={"100%"}
        sx={{ zIndex: theme.zIndex.modal + 1 }}
      >
        <WaveBG fullScreen={true} waveCount={8} />
        <UnsavedChanges
          open={openCancelDialog}
          onClose={cancelDiscard}
          onCancel={cancelDiscard}
          onConfirm={confirmDiscard}
        />
        <Box
          sx={{
            width: "100%",
            typography: "body1",
            position: "relative",
          }}
        >
          <TabContext value={tabValue}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: "background.paper",
                width: "100%",
                position: "sticky",
                top: -1,
                zIndex: theme.zIndex.appBar,
              }}
            >
              <Box sx={{ width: isMobil ? "100%" : "60%", margin: "0 auto" }}>
                <TabList
                  onChange={hdlChangeTab}
                  aria-label="tabs evaluation"
                  centered
                  sx={{
                    minHeight: 0,
                    height: "32px",
                    ".MuiTabs-flexContainer": {
                      alignItems: "center",
                    },
                    // styles by default
                    ".MuiTab-root": {
                      minHeight: 0,
                      height: "30px",
                      px: isMobil ? 1 : 1.5,
                      py: 0,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: isMobil ? 14 : 15,
                      minWidth: isMobil ? 100 : 130,
                      color: theme.palette.secondary.dark,
                      opacity: 0.7,
                      transition: "color 0.3s, opacity 0.3s",

                      "&:hover": {
                        opacity: 1,
                        color: theme.palette.text.primary,
                      },
                      // selected tab
                      "&.Mui-selected": {
                        color: theme.palette.primary.main,
                        opacity: 1,
                      },
                    },
                    ".MuiTabs-indicator": {
                      height: "3px",
                      borderRadius: "1px",
                      backgroundColor: theme.palette.primary.main,
                    },
                  }}
                >
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.value}
                      label={tab.label}
                      value={tab.value}
                      sx={{
                        fontSize: "1em",
                        fontWeight: 600,
                        textTransform: "none",
                      }}
                    />
                  ))}
                </TabList>
              </Box>
            </Box>
            {tabs.map((tab) => (
              <TabPanel
                key={tab.value}
                value={tab.value}
                sx={{ p: { xs: 1, sm: 2 } }}
                keepMounted
              >
                {tab.component}
              </TabPanel>
            ))}
          </TabContext>
        </Box>
      </Box>
    );
  }
);

Evaluation.displayName = "Evaluation";
